import { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { useApi } from "../hooks/useApi";
import { useTeamContext } from "../contexts/TeamContext";
import { useJourneyDetail, useUpdateJourney } from "../hooks/useJourneys";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faUsers,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";

import {
  contentItemsToRows,
  rowsToContentItems,
} from "../utils/contentFormatters";

import JourneyWizard from "../components/journeys/wizard/JourneyWizard";
import { JourneyAudienceContent } from "../components/journeys/detail/Audience";
import { JourneyResultsContent } from "../components/journeys/detail/Results";
import StatusBadge from "../components/journeys/StatusBadge";

import Spinner from "../components/spinner";
import Modal from "../components/modal";

import { JourneyStepInput, ContentSelectionType } from "../types/journey";
import { GameName } from "../types/games";

// Use simplified types as we follow an incremental approach
interface JourneyBasicInfo {
  name: string;
  description: string;
  touchpointCount: number;
}

// Define content rows structure for better type safety
interface WizardState {
  currentStep: number;
  journeyInfo: JourneyBasicInfo;
  touchpoints: JourneyStepInput[];
  contentRows: { [touchpointId: string]: any[] };
  selectedTemplate?: any;
}

// Define required content types
interface Category {
  id: string;
  name: string;
  description?: string;
}

interface GameType {
  id: string;
  name: string;
  description?: string;
  config?: any;
}

/**
 * Journey Edit Page component
 *
 * Allows users to edit an existing journey by reusing the journey creation wizard
 * with pre-populated data from the existing journey.
 */
// Define the tab types
type TabType = "edit" | "audience" | "results";

function JourneyEdit() {
  const navigate = useNavigate();
  const { journeyId } = useParams<{ journeyId: string }>();
  const { teamId } = useTeamContext();
  const api = useApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>("edit");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  // Track if the journey is editable based on its status
  const [isEditable, setIsEditable] = useState(true);

  // State for wizard
  const [wizardState, setWizardState] = useState<WizardState>({
    currentStep: 0,
    journeyInfo: {
      name: "",
      description: "",
      touchpointCount: 0,
    },
    touchpoints: [],
    contentRows: {},
  });

  // State for categories and game types
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [gameTypes, setGameTypes] = useState<GameType[]>([]);
  const [gameTypesLoading, setGameTypesLoading] = useState(false);

  // Fetch journey details
  const { data: journeyDetail, isLoading: journeyLoading } = useJourneyDetail(
    teamId,
    journeyId || "",
  );

  // Check if journey is editable based on its status
  useEffect(() => {
    if (journeyDetail) {
      // Only allow editing if status is DRAFT or COMPLETED
      const canEdit =
        journeyDetail.derived_status === "DRAFT" ||
        journeyDetail.derived_status === "COMPLETED";
      setIsEditable(canEdit);
    }
  }, [journeyDetail]);

  // Update journey mutation - using try-catch pattern to handle potential errors
  const updateJourneyMutation = useUpdateJourney(teamId, journeyId || "");

  // Memoize categories and game types
  const memoizedCategories = useMemo(() => categories, [categories]);
  const memoizedGameTypes = useMemo(() => gameTypes, [gameTypes]);

  // Combined loading state
  const isLoading = categoriesLoading || gameTypesLoading || journeyLoading;

  // Initialize active tab based on URL parameter or journey status
  useEffect(() => {
    // First check if tab is explicitly specified in URL
    const tabParam = searchParams.get("tab") as TabType;
    if (
      tabParam &&
      (tabParam === "edit" || tabParam === "audience" || tabParam === "results")
    ) {
      setActiveTab(tabParam);
    } else if (!searchParams.has("tab") && journeyDetail) {
      // No tab in URL, select best tab based on journey status
      let bestTab: TabType = "edit"; // Default

      if (
        journeyDetail.derived_status === "SCHEDULED" ||
        journeyDetail.derived_status === "LIVE"
      ) {
        bestTab = "audience";
      } else if (journeyDetail.derived_status === "COMPLETED") {
        bestTab = "results";
      }

      // Set active tab and update URL
      setActiveTab(bestTab);
      const newParams = new URLSearchParams(searchParams);
      newParams.set("tab", bestTab);
      setSearchParams(newParams);
    } else if (!searchParams.has("tab")) {
      // No tab and no journey detail yet, set default
      const newParams = new URLSearchParams(searchParams);
      newParams.set("tab", activeTab);
      setSearchParams(newParams);
    }
  }, [searchParams, setSearchParams, activeTab, journeyDetail]);

  // Fetch categories for content selection
  const fetchCategories = useCallback(async () => {
    if (!teamId) return;

    setCategoriesLoading(true);
    try {
      const response = await api.get(`/v2/games/categories?team=${teamId}`);
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      toast.error("Failed to load content categories");
    } finally {
      setCategoriesLoading(false);
    }
  }, [api, teamId]);

  // Fetch game types for journey creation
  const fetchGameTypes = useCallback(async () => {
    if (!teamId) return;

    setGameTypesLoading(true);
    try {
      const response = await api.get(`/v2/slack/game-types?team=${teamId}`);
      setGameTypes(response.data);
    } catch (error) {
      console.error("Failed to fetch game types:", error);
      toast.error("Failed to load game types");
    } finally {
      setGameTypesLoading(false);
    }
  }, [api, teamId]);

  // No need to require here since we imported at the top

  // Initialize wizard state from journey details
  useEffect(() => {
    if (!journeyDetail) return;

    // Initialize contentRows object with proper typing
    const initialContentRows: { [key: string]: any[] } = {};

    // Map journey details to wizard state
    const touchpoints: JourneyStepInput[] = journeyDetail.steps.map(
      (step, index) => {
        // Determine content selection type based on presence of custom content
        let contentSelectionType: ContentSelectionType = "surprise_me";
        if (step.custom_content && step.custom_content.length > 0) {
          contentSelectionType = "custom";

          // Convert custom_content items to contentRows for this touchpoint
          initialContentRows[index.toString()] = contentItemsToRows(
            step.custom_content,
          );
        }

        return {
          game_type_id: step.game_type_id,
          offset_days: step.offset_days,
          offset_hours: step.offset_hours,
          offset_minutes: step.offset_minutes,
          custom_message: step.custom_message,
          content_selection_type: contentSelectionType,
          custom_content: step.custom_content,
          game_config: step.game_config,
        };
      },
    );

    const journeyInfo: JourneyBasicInfo = {
      name: journeyDetail.name,
      description: journeyDetail.description || "",
      touchpointCount: journeyDetail.steps.length,
    };

    setWizardState({
      currentStep: 0,
      journeyInfo,
      touchpoints,
      contentRows: initialContentRows,
    });
  }, [journeyDetail]);

  // Fetch data when the component mounts
  useEffect(() => {
    fetchCategories();
    fetchGameTypes();
  }, [fetchCategories, fetchGameTypes]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    // Show custom modal instead of browser confirm
    setIsCancelModalOpen(true);
  }, []);

  // Handle actual cancellation after confirmation
  const confirmCancel = useCallback(() => {
    setIsCancelModalOpen(false);
    navigate(`/journeys?team=${teamId}`);
  }, [navigate, teamId]);

  // No need to require here since we imported at the top

  // Handle journey update completion
  // This function accepts touchpoints from the JourneyWizard component
  const handleComplete = useCallback(
    async (touchpoints?: JourneyStepInput[]) => {
      // Prevent updates if journey is not in an editable state
      if (!isEditable) {
        toast.error("This journey cannot be updated in its current state.");
        return;
      }

      try {
        // Use touchpoints provided by JourneyWizard if available, otherwise process from wizardState
        const processedTouchpoints =
          touchpoints ||
          wizardState.touchpoints.map((touchpoint, index) => {
            // Ensure offset_minutes is present, default to 0 if not
            const touchpointWithMinutes = {
              ...touchpoint,
              offset_minutes: touchpoint.offset_minutes ?? 0,
            };

            // If content selection type is custom, convert contentRows to custom_content
            if (
              touchpointWithMinutes.content_selection_type === "custom" &&
              wizardState.contentRows[index.toString()]
            ) {
              // Find the game type to get the name for conversion
              const gameType = gameTypes.find(
                (g) => g.id === touchpointWithMinutes.game_type_id,
              );
              // Convert string to GameName type to satisfy the type system
              const gameTypeName = gameType
                ? (gameType.name as GameName)
                : ("" as GameName);

              // Convert rows to content items
              const customContent = rowsToContentItems(
                wizardState.contentRows[index.toString()],
                gameTypeName,
                // We don't have touchpoint_id in the JourneyStepInput type
                // It will be assigned by the backend
              );

              return {
                ...touchpointWithMinutes,
                custom_content: customContent,
              };
            }
            return touchpointWithMinutes;
          });

        // Log to ensure all touchpoints have offset_minutes
        console.log("Processed Touchpoints:", processedTouchpoints);

        // Prepare journey update input
        const updatedJourney = {
          name: wizardState.journeyInfo.name,
          description: wizardState.journeyInfo.description,
          team: teamId,
          steps: processedTouchpoints,
          // Include channel_ids if we have them from the journeyDetail
          channel_ids:
            journeyDetail?.channels?.map((channel) => channel.channel_id) || [],
        };

        // Update the journey
        await updateJourneyMutation.mutateAsync(updatedJourney);

        // Show success message
        toast.success("Journey updated successfully");

        // Always navigate to the audience tab after successful update
        // This ensures consistent behavior regardless of how we got here
        navigate(`/journeys/edit/${journeyId}?team=${teamId}&tab=audience`);
      } catch (error) {
        console.error("Failed to update journey:", error);
        toast.error("Failed to update journey");
      }
    },
    [
      journeyId,
      teamId,
      wizardState,
      updateJourneyMutation,
      navigate,
      isEditable,
    ],
  );

  // Handle journey deletion
  const handleDeleteJourney = useCallback(async () => {
    if (!teamId || !journeyId) return;

    // Prevent deletion if journey is not in an editable state
    if (!isEditable) {
      toast.error("This journey cannot be deleted in its current state.");
      setIsDeleteModalOpen(false);
      return;
    }

    setIsDeleting(true);
    try {
      await api.delete(`/v2/slack/journeys/${journeyId}?team=${teamId}`);
      toast.success("Journey deleted successfully");
      navigate(`/journeys?team=${teamId}`);
    } catch (error) {
      console.error("Failed to delete journey:", error);
      toast.error("Failed to delete journey");
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  }, [api, journeyId, teamId, navigate, isEditable]);

  // We're using the StatusBadge component which handles colors consistently

  // Handle tab change
  const handleTabChange = (tab: TabType, e: React.MouseEvent) => {
    // Only prevent default for tab navigation buttons
    if (e.currentTarget.tagName === "BUTTON") {
      e.preventDefault();
    }

    setActiveTab(tab);
    // Update URL parameter
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", tab);
    setSearchParams(newParams);
  };

  // Tab navigation component
  const TabNavigation = () => (
    <div className="border-t border-gray-200">
      <div className="flex">
        <button
          onClick={(e) => handleTabChange("edit", e)}
          className={`px-6 py-3 font-medium text-sm flex items-center ${activeTab === "edit" ? "text-brand rounded-bl-lg border-b-4 border-brand" : "text-gray-500 hover:text-gray-700 border-b-4 border-transparent"}`}
        >
          <FontAwesomeIcon icon={faEdit} className="mr-2" />
          Edit
        </button>

        <button
          onClick={(e) => handleTabChange("audience", e)}
          className={`px-6 py-3 font-medium text-sm flex items-center ${activeTab === "audience" ? "text-brand border-b-4 border-brand" : "text-gray-500 hover:text-gray-700 border-b-4 border-transparent"}`}
        >
          <FontAwesomeIcon icon={faUsers} className="mr-2" />
          Audience
        </button>

        <button
          onClick={(e) => handleTabChange("results", e)}
          className={`px-6 py-3 font-medium text-sm flex items-center ${activeTab === "results" ? "text-brand border-b-4 border-brand" : "text-gray-500 hover:text-gray-700 border-b-4 border-transparent"}`}
        >
          <FontAwesomeIcon icon={faChartLine} className="mr-2" />
          Results
        </button>
      </div>
    </div>
  );

  if (isLoading) {
    return <Spinner />;
  }

  if (!journeyDetail && !journeyLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <h1 className="text-2xl font-bold mb-4">Journey Not Found</h1>
        <p className="mb-4">
          The journey you are trying to edit does not exist.
        </p>
      </div>
    );
  }

  // Render different content based on active tab
  const renderTabContent = () => {
    // If journey is not editable and we're on the edit tab, show message explaining why
    if (activeTab === "edit" && !isEditable && journeyDetail) {
      return (
        <div className="p-8 text-center">
          <h3 className="text-xl font-semibold mb-4">
            This journey cannot be edited
          </h3>
          <p className="mb-4 text-gray-600 text-md">
            Journeys that are currently{" "}
            {journeyDetail.derived_status.toLowerCase()} cannot be edited. Only
            journeys in draft or completed status can be modified.
          </p>
        </div>
      );
    }

    switch (activeTab) {
      case "edit":
        return (
          <JourneyWizard
            categories={memoizedCategories}
            gameTypes={memoizedGameTypes}
            categoriesLoading={categoriesLoading}
            gameTypesLoading={gameTypesLoading}
            wizardState={wizardState as any}
            onWizardStateChange={(state: any) => setWizardState(state)}
            onCancel={handleCancel}
            onComplete={handleComplete}
            onDelete={() => setIsDeleteModalOpen(true)}
            isEdit={true}
          />
        );
      case "audience":
        return <JourneyAudienceContent />;
      case "results":
        return <JourneyResultsContent />;
      default:
        return <div>Invalid tab</div>;
    }
  };

  return (
    <div className="container mx-auto">
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-3 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              {journeyDetail?.name || "Edit Journey"}
            </h2>
            {journeyDetail && (
              <div className="scale-110">
                {" "}
                {/* Slightly larger for better visibility */}
                <StatusBadge status={journeyDetail.derived_status} />
              </div>
            )}
          </div>
          <div>
            <p className="text-sm text-gray-600 mt-1 px-6 pb-2">
              Edit your journey, publish to an audience, and view results
            </p>
          </div>
          <TabNavigation />
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          {renderTabContent()}
        </div>
      </div>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Delete Journey
          </h3>
          <p className="mb-6 text-gray-600">
            Are you sure you want to delete this journey? This action cannot be
            undone.
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteJourney}
              disabled={isDeleting}
              className="px-4 py-2 rounded-md bg-red-500 text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:bg-red-300"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Cancel confirmation modal */}
      <Modal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
      >
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Cancel Editing
          </h3>
          {journeyDetail &&
          (journeyDetail.derived_status === "SCHEDULED" ||
            journeyDetail.derived_status === "LIVE") ? (
            <div className="mb-6">
              <div className="p-3 bg-amber-50 border border-amber-200 text-amber-700 rounded-md mb-4">
                <p className="font-medium mb-1">
                  Warning: This journey is currently{" "}
                  {journeyDetail.derived_status.toLowerCase()}
                </p>
                <p>
                  Any unsaved changes will be lost, but the scheduled games and
                  existing journey will not be affected.
                </p>
              </div>
              <p className="text-gray-600">
                If you want to cancel scheduled games, you'll need to do that
                from the Audience tab.
              </p>
            </div>
          ) : (
            <p className="mb-6 text-gray-600">
              Are you sure you want to cancel editing this journey? Any unsaved
              changes will be lost.
            </p>
          )}
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setIsCancelModalOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Go Back
            </button>
            <button
              onClick={confirmCancel}
              className="px-4 py-2 rounded-md bg-gray-800 text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Leave Page
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default JourneyEdit;
